import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrg } from '../context/OrgContext';

const InsuranceSubmission = () => {
  const { legalName } = useOrg();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Extract parameters from URL
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');
  const userId = urlParams.get('userId');
  const requestedPolicyType = urlParams.get('policyType');

  // Form state
  const [formData, setFormData] = useState({
    policyType: requestedPolicyType || '',
    companyName: '',
    policyNumber: '',
    contactName: '',
    contactPhone: '',
    contactFax: '',
    contactEmail: '',
    coverageAmount: '',
    effectiveDate: '',
    expirationDate: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US'
    }
  });

  const [coiFile, setCoiFile] = useState(null);

  const policyTypes = [
    'General Liability',
    'Commercial Auto',
    'Workers Compensation',
    'Professional Liability',
    'Garage Liability',
    'Cargo Insurance',
    'Umbrella Policy'
  ];

  // Check if user is logged in and validate token
  useEffect(() => {
    const checkAuthAndToken = async () => {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        // Redirect to login with return URL
        navigate(`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`);
        return;
      }

      // Validate that we have the required URL parameters
      if (!token || !userId) {
        setError('Invalid submission link. Missing required parameters.');
        setLoading(false);
        return;
      }

      try {
        const headers = { Authorization: `Bearer ${authToken}` };
        
        // Get current user info to check permissions
        const userResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/me`,
          { headers }
        );
        
        const currentUser = userResponse.data;
        
        // Check if user has permission to submit for this vendor ID
        console.log('Permission check:', {
          userRole: currentUser.primaryRole,
          userVendorId: currentUser.vendorId,
          userVendorNumber: currentUser.vendorNumber,
          userId: currentUser._id,
          urlUserId: userId,
          urlUserIdMatch: currentUser._id === userId,
          hasAdminRole: currentUser.secondaryRoles?.admin,
          secondaryRoles: currentUser.secondaryRoles
        });

        let canSubmit = false;
        
        // OW users can access any submission (for testing/admin purposes)
        if (currentUser.primaryRole === 'OW' || currentUser.primaryRole === 'sOW') {
          canSubmit = true;
        }
        // Users with admin secondary role can submit if their vendor ID matches or user ID matches
        else if (currentUser.secondaryRoles?.admin === true) {
          canSubmit = currentUser._id === userId ||
                     currentUser.vendorId === userId ||
                     currentUser.vendorNumber === userId;
        }

        if (!canSubmit) {
          setError(`You do not have permission to submit insurance policies for this vendor. You need admin role and matching vendor ID. User ID: ${currentUser._id}, URL User ID: ${userId}, Role: ${currentUser.primaryRole}, Admin: ${currentUser.secondaryRoles?.admin}`);
          setLoading(false);
          return;
        }

        // Check if this submission was already completed
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/insurance/submission-status?token=${token}&userId=${userId}`,
          { headers }
        );
        
        if (response.data.submitted) {
          setSubmitted(true);
        }
        
        // If we get here, user is authenticated and has permission
        setLoading(false);
        
      } catch (err) {
        console.error('Error checking submission status:', err);
        if (err.response?.status === 403) {
          setError('You do not have permission to access this submission.');
        } else if (err.response?.status === 401) {
          // Token is invalid, redirect to login
          navigate(`/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`);
          return;
        } else {
          setError('Failed to validate submission link. Please contact support.');
        }
        setLoading(false);
      }
    };

    checkAuthAndToken();
  }, [token, userId, location, navigate]);

  // Handle form field changes
  const handleChange = (field, value) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle file upload
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, JPEG, or PNG file.');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB.');
        return;
      }
      
      setCoiFile(file);
      setError('');
    }
  };

  // Validate form
  const validateForm = () => {
    const required = [
      'policyType', 'companyName', 'policyNumber', 'contactName', 
      'contactPhone', 'contactEmail', 'effectiveDate', 'expirationDate',
      'address.street1', 'address.city', 'address.state', 'address.zip'
    ];

    for (const field of required) {
      if (field.startsWith('address.')) {
        const addressField = field.split('.')[1];
        if (!formData.address[addressField]) {
          return `${addressField.charAt(0).toUpperCase() + addressField.slice(1)} is required.`;
        }
      } else if (!formData[field]) {
        return `${field.charAt(0).toUpperCase() + field.slice(1)} is required.`;
      }
    }

    if (!coiFile) {
      return 'Certificate of Insurance (COI) file is required.';
    }

    return null;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        navigate('/login');
        return;
      }

      const headers = { Authorization: `Bearer ${authToken}` };
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('token', token);
      submitData.append('userId', userId);
      submitData.append('policyData', JSON.stringify(formData));
      if (coiFile) {
        submitData.append('coiFile', coiFile);
      }

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/insurance/submit-policy`,
        submitData,
        { 
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccess(true);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting insurance policy:', err);
      setError(err.response?.data?.message || 'Failed to submit insurance policy. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading screen while checking authentication and permissions
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading submission form...
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Show error if there's an issue
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error">
            {error}
          </Alert>
        </Paper>
      </Container>
    );
  }

  // If already submitted, show submitted status
  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main" gutterBottom>
              Policy Submitted
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your insurance policy information has been successfully submitted.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This submission has been completed and no further action is required.
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Add New Insurance Policy
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Please provide your insurance policy information and upload your Certificate of Insurance (COI).
          {legalName} must be listed as additionally insured.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Insurance policy submitted successfully!
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Policy Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Policy Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Policy Type</InputLabel>
                <Select
                  value={formData.policyType}
                  onChange={(e) => handleChange('policyType', e.target.value)}
                  label="Policy Type"
                >
                  {policyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Insurance Company Name"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Policy Number"
                value={formData.policyNumber}
                onChange={(e) => handleChange('policyNumber', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Coverage Amount"
                value={formData.coverageAmount}
                onChange={(e) => handleChange('coverageAmount', e.target.value)}
                placeholder="e.g., $1,000,000"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Effective Date"
                type="date"
                value={formData.effectiveDate}
                onChange={(e) => handleChange('effectiveDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Expiration Date"
                type="date"
                value={formData.expirationDate}
                onChange={(e) => handleChange('expirationDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Agent Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Agent Name"
                value={formData.contactName}
                onChange={(e) => handleChange('contactName', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Agent Phone"
                value={formData.contactPhone}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Agent Fax"
                value={formData.contactFax}
                onChange={(e) => handleChange('contactFax', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Agent Email"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => handleChange('contactEmail', e.target.value)}
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Insurance Company Address
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Street Address"
                value={formData.address.street1}
                onChange={(e) => handleChange('address.street1', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address 2 (Optional)"
                value={formData.address.street2}
                onChange={(e) => handleChange('address.street2', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="City"
                value={formData.address.city}
                onChange={(e) => handleChange('address.city', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="State"
                value={formData.address.state}
                onChange={(e) => handleChange('address.state', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="ZIP Code"
                value={formData.address.zip}
                onChange={(e) => handleChange('address.zip', e.target.value)}
              />
            </Grid>

            {/* File Upload */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Certificate of Insurance (COI)
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ border: '2px dashed #ccc', borderRadius: 1, p: 3, textAlign: 'center' }}>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="coi-upload"
                />
                <label htmlFor="coi-upload">
                  <Button variant="outlined" component="span" sx={{ mb: 1 }}>
                    Choose COI File
                  </Button>
                </label>
                <Typography variant="body2" color="text.secondary">
                  Upload PDF, JPEG, or PNG (max 10MB)
                </Typography>
                {coiFile && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    Selected: {coiFile.name}
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ minWidth: 200 }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit Policy'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default InsuranceSubmission;